import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { useMattersStore } from '@/lib/mattersStore';
import { useApiBarristersStore } from '@/lib/barristersApi';
import { fetchCourts, fetchJudges, type Court, type Judge } from '@/lib/courtsApi';
import { fetchPracticeAreas, type PracticeArea } from '@/lib/practiceAreasApi';
import { createJudgment } from '@/lib/judgmentsApi';
import { uploadDocument } from '@/lib/documentsApi';
import { useJudgmentsStore } from '@/lib/judgmentsStore';
import { useAuthStore } from '@/features/auth/store';
import { canCreateJudgment } from '@/lib/pupilUiBoundary';
import { EmptyState } from '@/components/shared/EmptyState';
import { toast } from 'sonner';
import {
  BookOpen,
  CircleNotch,
  Paperclip,
  X,
  CaretDown,
} from '@phosphor-icons/react';
import { PageHeader } from '@/components/shared/PageHeader';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { ExpandableTextarea } from '@/components/ui/expandable-textarea';
import { DOCUMENT_ACCEPT_ATTR } from '@jurivoxa/shared';

const OUTCOMES = [
  { value: 'won',       label: 'Won'       },
  { value: 'lost',      label: 'Lost'      },
  { value: 'partial',   label: 'Partial'   },
  { value: 'settled',   label: 'Settled'   },
  { value: 'dismissed', label: 'Dismissed' },
  { value: 'withdrawn', label: 'Withdrawn' },
] as const;

/**
 * A neutral citation is the court's own reference for a handed-down judgment — a four-digit year
 * in brackets, the court's letters, a number, and sometimes a division:
 *
 *   [2026] EWHC 123 (Ch)   ·   [2026] EWCA Civ 456   ·   [2026] UKSC 12   ·   [2026] CSOH 45
 *
 * The SHAPE is checked, never the court code. There are dozens of valid codes across England,
 * Scotland and Northern Ireland, and new ones appear (the Crown Court only gained `EWCR` in
 * 2022) — an allowlist would eventually refuse a real judgment, which is far worse than letting
 * an odd one through. Anything citation-shaped passes whatever court it came from.
 *
 * Blank stays valid: most County Court trials and interim applications produce an order, not a
 * citeable judgment.
 */
const CITATION_SHAPE = /^\[\d{4}\]\s+[A-Za-z]+(?:\s+[A-Za-z]+)*\s+[A-Za-z]?\d+(?:\s*\([A-Za-z ]+\))?$/;
const CITATION_HINT = 'A neutral citation looks like [2026] EWHC 123 (Ch). Leave blank if the judgment has none.';

const sel = 'flex h-9 w-full rounded-lg border border-border/70 bg-card px-3 text-sm text-foreground focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-100/50';
const lbl = 'block text-xs font-medium text-foreground mb-1.5';

export default function NewJudgmentPage() {
  const navigate = useNavigate();
  const matters = useMattersStore((s) => s.matters);
  const fetchMatters = useMattersStore((s) => s.fetchMatters);
  const barristers = useApiBarristersStore((s) => s.barristers);
  const fetchBarristers = useApiBarristersStore((s) => s.fetch);
  const fetchJudgments = useJudgmentsStore((s) => s.fetchJudgments);
  const memberType = useAuthStore((s) => s.user?.memberType);

  const [courts, setCourts] = useState<Court[]>([]);
  const [practiceAreas, setPracticeAreas] = useState<PracticeArea[]>([]);
  const [judges, setJudges] = useState<Judge[]>([]);
  const [refLoading, setRefLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchMatters();
    fetchBarristers();
    let done = 0;
    const finish = () => { if (++done === 2) setRefLoading(false); };
    fetchCourts()
      .then((c) => setCourts(c.filter((x) => x.isActive)))
      .catch(() => toast.error('Could not load courts'))
      .finally(finish);
    fetchPracticeAreas()
      .then((p) => setPracticeAreas(p.filter((x) => x.isActive)))
      .catch(() => toast.error('Could not load practice areas'))
      .finally(finish);
  }, [fetchMatters, fetchBarristers]);

  const [citation, setCitation] = useState('');
  const [matterId, setMatterId] = useState('');
  const [caseName, setCaseName] = useState('');
  const [courtId, setCourtId] = useState('');
  const [judgeId, setJudgeId] = useState('');
  const [date, setDate] = useState('');
  const [outcome, setOutcome] = useState<typeof OUTCOMES[number]['value'] | ''>('');
  /** Which required fields were empty on the last submit — drives the red outline below. */
  const [missing, setMissing] = useState({ matter: false, date: false, outcome: false });
  /** Set when a citation was typed but is not citation-shaped. Blank is always fine. */
  const [citationInvalid, setCitationInvalid] = useState(false);
  const [practiceAreaId, setPracticeAreaId] = useState('');
  const [linkedBarristerId, setLinkedBarristerId] = useState('');
  const [headnote, setHeadnote] = useState('');
  const [keyPoints, setKeyPoints] = useState('');
  const [isPublic, setIsPublic] = useState(false);
  const [tags, setTags] = useState('');
  const [orderFile, setOrderFile] = useState<File | null>(null);

  // Load judges when court changes
  useEffect(() => {
    if (!courtId) { setJudges([]); setJudgeId(''); return; }
    fetchJudges(courtId)
      .then(setJudges)
      .catch(() => setJudges([]));
  }, [courtId]);

  const handleMatter = (id: string) => {
    setMatterId(id);
    const m = matters.find((x) => x.id === id);
    if (m) { setCaseName(m.name); }
  };

  // Progressive disclosure — six stacked cards ran this form past two screens. Content, the
  // court order and publication are all optional; each forces itself open the moment it holds
  // anything, so nothing typed can hide behind a collapsed heading.
  const [showContent, setShowContent] = useState(false);
  const [showOrder, setShowOrder] = useState(false);
  const [showPublication, setShowPublication] = useState(false);

  const handleCourt = (id: string) => {
    setCourtId(id);
    setJudgeId('');
  };

  const submit = async () => {
    // The form used to refuse with a toast alone, naming the field in words while leaving every
    // field looking identical — on a long form that is a hunt. Mark the offending field too, the
    // way the matter and client forms already do.
    setMissing({ matter: !matterId, date: !date, outcome: !outcome });
    const citationTyped = citation.trim();
    const citationBad = citationTyped !== '' && !CITATION_SHAPE.test(citationTyped);
    setCitationInvalid(citationBad);
    if (!matterId) { toast.error('Related matter is required'); return; }
    // Neutral citation is intentionally optional — only reported/handed-down judgments have one.
    // Most County Court trials and interim applications produce an order, not a citeable judgment.
    if (!date) { toast.error('Judgment date is required'); return; }
    if (!outcome) { toast.error('Outcome is required'); return; }
    if (citationBad) { toast.error(CITATION_HINT); return; }

    setSubmitting(true);
    try {
      // Upload the court order first (if provided) so it becomes a real, connected document
      // on the matter — then link it to the judgment we create. If upload fails we stop here
      // rather than record a judgment that silently lost its order.
      let orderDocumentId: string | undefined;
      if (orderFile) {
        const doc = await uploadDocument(orderFile, { caseId: matterId, category: 'order' });
        orderDocumentId = doc.id;
      }

      await createJudgment({
        caseName: caseName.trim() || matters.find(m => m.id === matterId)?.name || '',
        caseId: matterId,
        neutralCitation: citation.trim() || undefined,
        courtId: courtId || undefined,
        judgeId: judgeId || undefined,
        judgmentDate: date,
        outcome: outcome as typeof OUTCOMES[number]['value'],
        practiceAreaId: practiceAreaId || undefined,
        linkedBarristerId: linkedBarristerId || undefined,
        summary: headnote.trim() || undefined,
        keyPoints: keyPoints.trim() || undefined,
        isPublic,
        tags: tags.split(',').map(t => t.trim()).filter(Boolean),
        orderDocumentId,
      });
      await fetchJudgments();
      toast.success('Judgment added successfully');
      navigate(-1);
    } catch (err: any) {
      toast.error(err?.response?.data?.error?.message ?? 'Could not save judgment');
    } finally {
      setSubmitting(false);
    }
  };

  const contentOpen = showContent || !!headnote.trim() || !!keyPoints.trim();
  const orderOpen = showOrder || !!orderFile;
  const publicationOpen = showPublication || isPublic || !!tags.trim();

  const selectedCourt = courts.find(c => c.id === courtId);
  const selectedOutcomeLabel = OUTCOMES.find(o => o.value === outcome)?.label;

  const outcomeBadgeVariant = (v: string) =>
    v === 'won' ? 'success' : v === 'lost' ? 'danger' : v === 'settled' ? 'warning' : 'default';

  // A PUPIL HOLDS `judgment.read.any` AND NO WRITE KEY — judgments.routes.ts `WRITE_ROLES` excludes
  // them, so `POST /judgments` answers 403. Both "Add judgment" buttons are already withheld
  // (MyJudgmentsPage); the `/*/judgments/new` routes were not, and this submit uploads the court
  // order FIRST (:113-120) so the judgment never loses it. A pupil DOES hold `document.create`, so
  // that upload succeeded and only `POST /judgments` then refused — leaving a real order document
  // on the matter attached to no judgment, which the pupil cannot remove either (no
  // `document.delete`). Exactly the orphan recorded as KG-WT-04. Withhold the form, name the reason.
  //
  // After every hook, so hook order does not vary by role. The server still decides.
  if (!canCreateJudgment(memberType)) {
    return (
      <div>
        <PageHeader title="Add Judgment" onBack={() => navigate(-1)} backLabel="Back to judgments" />
        <EmptyState
          icon={<BookOpen className="h-6 w-6" />}
          title="Recording a judgment isn't available to your role"
          description="Judgments are written up by the tenants on the matter. Every judgment on a matter you are assigned to is yours to read."
        />
      </div>
    );
  }

  return (
    <div>
      <PageHeader title="Add Judgment" onBack={() => navigate(-1)} backLabel="Back to judgments" />

      <div className="grid gap-6 lg:grid-cols-3">
        {/* ── Main column ── */}
        <div className="space-y-6 lg:col-span-2">

          {/* The judgment itself — citation, case, court and judge in one card */}
          <Card>
            <CardHeader><CardTitle>The judgment</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className={lbl}>Neutral citation</label>
                <Input
                  placeholder="[2026] EWHC 1234 (KB)"
                  className={cn(citationInvalid && 'border-danger ring-2 ring-danger/20')}
                  value={citation}
                  onChange={(e) => { setCitationInvalid(false); setCitation(e.target.value); }}
                />
                <p className={cn('mt-1 text-xs', citationInvalid ? 'text-danger' : 'text-muted-foreground')}>
                  {citationInvalid
                    ? CITATION_HINT
                    : 'Reported judgments only — leave blank for County Court orders and interim results.'}
                </p>
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label className={lbl}>Related matter *</label>
                  <select
                    className={cn(sel, missing.matter && 'border-danger ring-2 ring-danger/20')}
                    value={matterId}
                    onChange={(e) => { setMissing((m) => ({ ...m, matter: false })); handleMatter(e.target.value); }}
                  >
                    <option value="">Select matter</option>
                    {matters.map((m) => (
                      <option key={m.id} value={m.id}>{m.number} — {m.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className={lbl}>Case name *</label>
                  <Input placeholder="e.g. Smith v Jones" value={caseName} onChange={(e) => setCaseName(e.target.value)} />
                </div>
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label className={lbl}>Court</label>
                  <select className={sel} value={courtId} onChange={(e) => handleCourt(e.target.value)} disabled={refLoading}>
                    <option value="">{refLoading ? 'Loading…' : 'Select court'}</option>
                    {courts.map((c) => (
                      <option key={c.id} value={c.id}>{c.shortName} — {c.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className={lbl}>Judgment date *</label>
                  <Input
                    type="date"
                    className={cn(missing.date && 'border-danger ring-2 ring-danger/20')}
                    value={date}
                    onChange={(e) => { setMissing((m) => ({ ...m, date: false })); setDate(e.target.value); }}
                  />
                </div>
              </div>
              <div>
                <label className={lbl}>Judge / bench</label>
                {judges.length > 0 ? (
                  <select className={sel} value={judgeId} onChange={(e) => setJudgeId(e.target.value)}>
                    <option value="">Select judge</option>
                    {judges.map((j) => (
                      <option key={j.id} value={j.id}>{j.title} {j.fullName}</option>
                    ))}
                  </select>
                ) : (
                  <Input
                    placeholder={courtId ? 'No judges on record for this court — type manually' : 'Select a court first, or type a name'}
                    disabled
                  />
                )}
              </div>
            </CardContent>
          </Card>

          {/* Outcome */}
          <Card>
            <CardHeader>
              <CardTitle className={cn(missing.outcome && 'text-danger')}>
                Outcome *{missing.outcome && <span className="ml-2 text-xs font-normal">— choose one</span>}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex flex-wrap gap-1.5">
                {OUTCOMES.map((o) => (
                  <button
                    key={o.value}
                    type="button"
                    onClick={() => { setMissing((m) => ({ ...m, outcome: false })); setOutcome(o.value); }}
                    className={`rounded-md border px-3 py-1.5 text-xs font-medium transition ${
                      outcome === o.value
                        ? 'border-brand-600 bg-brand-600 text-white'
                        : 'border-border bg-card text-muted-foreground hover:border-brand-300 hover:text-foreground'
                    }`}
                  >
                    {o.label}
                  </button>
                ))}
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label className={lbl}>Practice area</label>
                  <select className={sel} value={practiceAreaId} onChange={(e) => setPracticeAreaId(e.target.value)} disabled={refLoading}>
                    <option value="">{refLoading ? 'Loading…' : 'Select area'}</option>
                    {practiceAreas.map((a) => (
                      <option key={a.id} value={a.id}>{a.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className={lbl}>Linked barrister</label>
                  <select className={sel} value={linkedBarristerId} onChange={(e) => setLinkedBarristerId(e.target.value)}>
                    <option value="">Select barrister</option>
                    {barristers.map((b) => (
                      <option key={b.id} value={b.id}>{b.fullName}</option>
                    ))}
                  </select>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Content — optional, folded until asked for */}
          <Card>
              <button
                type="button"
                onClick={() => setShowContent((v) => !v)}
                className="flex w-full items-center justify-between px-5 py-3 text-left"
              >
                <span className="text-sm font-semibold text-foreground">
                  Headnote &amp; key points
                  <span className="ml-1.5 text-xs font-normal text-muted-foreground">(optional)</span>
                </span>
                <CaretDown className={`h-4 w-4 text-muted-foreground transition-transform ${contentOpen ? 'rotate-180' : ''}`} />
              </button>
            {contentOpen && (
            <CardContent className="space-y-4 border-t border-border pt-4">
              <div>
                <label className={lbl}>Headnote / summary</label>
                <ExpandableTextarea
                  label="Headnote / summary"
                  rows={4}
                  placeholder="Brief summary of the judgment…"
                  value={headnote}
                  onChange={setHeadnote}
                />
              </div>
              <div>
                <label className={lbl}>Key points</label>
                <ExpandableTextarea
                  label="Key points"
                  rows={4}
                  placeholder="One point per line"
                  value={keyPoints}
                  onChange={setKeyPoints}
                />
              </div>
            </CardContent>
            )}
          </Card>

          {/* Court order — optional, folded until asked for */}
          <Card>
              <button
                type="button"
                onClick={() => setShowOrder((v) => !v)}
                className="flex w-full items-center justify-between px-5 py-3 text-left"
              >
                <span className="text-sm font-semibold text-foreground">
                  Court order
                  <span className="ml-1.5 text-xs font-normal text-muted-foreground">(optional — can be uploaded later)</span>
                </span>
                <CaretDown className={`h-4 w-4 text-muted-foreground transition-transform ${orderOpen ? 'rotate-180' : ''}`} />
              </button>
            {orderOpen && (
            <CardContent className="space-y-3 border-t border-border pt-4">
              <p className="text-xs text-muted-foreground">
                The sealed order (PDF or Word), filed against the matter&apos;s documents.
              </p>
              {orderFile ? (
                <div className="flex items-center justify-between gap-3 rounded-lg border border-border bg-muted/30 px-3 py-2.5">
                  <div className="flex min-w-0 items-center gap-2">
                    <Paperclip className="h-4 w-4 shrink-0 text-muted-foreground" />
                    <span className="truncate text-sm text-foreground">{orderFile.name}</span>
                    <span className="shrink-0 text-xs text-muted-foreground">
                      {orderFile.size < 1024 * 1024
                        ? `${(orderFile.size / 1024).toFixed(0)} KB`
                        : `${(orderFile.size / (1024 * 1024)).toFixed(1)} MB`}
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={() => setOrderFile(null)}
                    disabled={submitting}
                    className="shrink-0 text-muted-foreground hover:text-foreground disabled:opacity-50"
                    aria-label="Remove file"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ) : (
                <label className="flex cursor-pointer items-center justify-center gap-2 rounded-lg border border-dashed border-border/70 bg-card px-3 py-4 text-sm text-muted-foreground transition hover:border-brand-300 hover:text-foreground">
                  <Paperclip className="h-4 w-4" />
                  Choose court order file
                  <input
                    type="file"
                    accept={DOCUMENT_ACCEPT_ATTR}
                    className="hidden"
                    onChange={(e) => setOrderFile(e.target.files?.[0] ?? null)}
                  />
                </label>
              )}
            </CardContent>
            )}
          </Card>

          {/* Publication — optional, folded until asked for */}
          <Card>
              <button
                type="button"
                onClick={() => setShowPublication((v) => !v)}
                className="flex w-full items-center justify-between px-5 py-3 text-left"
              >
                <span className="text-sm font-semibold text-foreground">
                  Publication &amp; tags
                  <span className="ml-1.5 text-xs font-normal text-muted-foreground">(optional)</span>
                </span>
                <CaretDown className={`h-4 w-4 text-muted-foreground transition-transform ${publicationOpen ? 'rotate-180' : ''}`} />
              </button>
            {publicationOpen && (
            <CardContent className="space-y-4 border-t border-border pt-4">
              <label className="flex items-center gap-2 text-sm text-foreground cursor-pointer">
                <input type="checkbox" checked={isPublic} onChange={(e) => setIsPublic(e.target.checked)}
                  className="h-4 w-4 rounded border-border text-brand-600 dark:text-brand-400 focus:ring-brand-500" />
                Public on chambers website
              </label>
              <div>
                <label className={lbl}>Tags <span className="font-normal text-muted-foreground">(comma separated)</span></label>
                <Input placeholder="Commercial, Hearsay, Appeals" value={tags} onChange={(e) => setTags(e.target.value)} />
              </div>
            </CardContent>
            )}
          </Card>
        </div>

        {/* ── Right sidebar ── */}
        <div className="space-y-4">
          <Card>
            <CardHeader><CardTitle>Preview</CardTitle></CardHeader>
            <CardContent>
              <div className="flex flex-col items-center gap-3 text-center">
                <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-brand-50 text-brand-600 dark:text-brand-400">
                  <BookOpen className="h-6 w-6" />
                </div>
                {citation ? (
                  <Badge variant="info" className="text-xs">{citation}</Badge>
                ) : (
                  <span className="text-xs text-muted-foreground">No citation yet</span>
                )}
                {outcome && <Badge variant={outcomeBadgeVariant(outcome)}>{selectedOutcomeLabel}</Badge>}
                {caseName && <p className="text-sm font-medium text-foreground">{caseName}</p>}
                {selectedCourt && <p className="text-xs text-muted-foreground">{selectedCourt.shortName}</p>}
              </div>
            </CardContent>
          </Card>

          <Button className="w-full" variant="primary" size="lg" onClick={submit} disabled={submitting || refLoading}>
            {submitting ? <><CircleNotch className="mr-2 h-4 w-4 animate-spin" />Saving…</> : 'Add judgment'}
          </Button>
          <Button className="w-full" variant="outline" size="lg" onClick={() => navigate(-1)} disabled={submitting}>
            Cancel
          </Button>
        </div>
      </div>
    </div>
  );
}
